import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/concepts/api-management')({
  component: APIManagementPage,
  head: () => ({
    meta: [
      { title: 'API Management Explained | Matricks' },
      { name: 'description', content: 'Learn about API management, its components, and best practices. Complete guide to managing the full API lifecycle from design to retirement.' },
    ],
  }),
});

const components = [
  {
    iconName: "Globe",
    title: "API Gateway",
    description: "Runtime component that handles request routing, authentication, rate limiting, and policy enforcement.",
  },
  {
    iconName: "Code",
    title: "Developer Portal",
    description: "Self-service portal where developers discover APIs, access documentation, and manage their API keys.",
  },
  {
    iconName: "Shield",
    title: "Security & Governance",
    description: "Policies and controls for authentication, authorization, data protection, and compliance.",
  },
  {
    iconName: "BarChart3",
    title: "Analytics & Monitoring",
    description: "Insights into API usage, performance metrics, error rates, and business-level analytics.",
  },
  {
    iconName: "Users",
    title: "Access Management",
    description: "Control who can access which APIs, manage subscriptions, and enforce usage quotas.",
  },
  {
    iconName: "Settings",
    title: "Lifecycle Management",
    description: "Tools for versioning, deprecation, and managing APIs from creation to retirement.",
  },
];

const lifecycle = [
  { phase: "Design", description: "Define API contracts, data models, and specifications using OpenAPI or similar standards." },
  { phase: "Develop", description: "Build the API implementation, create mock servers, and establish testing strategies." },
  { phase: "Secure", description: "Implement authentication, authorization, and threat protection measures." },
  { phase: "Publish", description: "Deploy the API, create documentation, and onboard it to the developer portal." },
  { phase: "Manage", description: "Monitor performance, enforce policies, and manage access and quotas." },
  { phase: "Analyze", description: "Track usage patterns, identify optimization opportunities, and measure business impact." },
  { phase: "Retire", description: "Deprecate and eventually sunset APIs while supporting migration to newer versions." },
];

const faqItems = [
  {
    question: "What's the difference between API management and an API gateway?",
    answer: "An API gateway is a runtime component that handles request processing. API management is a broader discipline that includes the gateway plus design tools, developer portals, analytics, and lifecycle management. The gateway is one part of a complete API management solution.",
  },
  {
    question: "Do I need API management for internal APIs?",
    answer: "Yes, internal APIs benefit from management too. Even if you don't need a public developer portal, internal teams need discoverability, documentation, and governance. API management helps maintain consistency and quality across internal services.",
  },
  {
    question: "How does API management support microservices?",
    answer: "API management provides the infrastructure for microservices to communicate securely and reliably. It offers service discovery, load balancing, circuit breaking, and observability—essential capabilities for microservices architectures.",
  },
  {
    question: "What is an API-first approach?",
    answer: "API-first means designing the API before implementing backend services. Teams agree on API contracts upfront, enabling parallel development. The API specification becomes the source of truth that guides implementation, testing, and documentation.",
  },
  {
    question: "How do I measure API success?",
    answer: "Key metrics include adoption (number of consumers, API calls), reliability (uptime, error rates), performance (latency, throughput), and business impact (revenue generated, cost savings). Good API management platforms provide these analytics out of the box.",
  },
];

function APIManagementPage() {
  return (
    <div>
      <ContentHeader
        title="API Management Explained"
        description="API management encompasses the processes, policies, and tools for creating, publishing, securing, monitoring, and analyzing APIs throughout their entire lifecycle."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Concepts", href: "/concepts" },
          { label: "API Management" },
        ]}
        badge="Concept"
      />

      {/* What is API Management */}
      <ContentSection id="what-is-api-management" title="Understanding API Management">
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-lg text-muted-foreground leading-relaxed">
            API management goes beyond simply running an API gateway. It's a comprehensive approach
            to treating APIs as products—with their own lifecycle, consumers, and business value.
          </p>

          <p className="text-muted-foreground leading-relaxed mt-4">
            Modern businesses expose hundreds or thousands of APIs to internal teams, partners, and
            the public. Without proper management, this leads to inconsistency, security gaps, and
            poor developer experience. API management provides the governance and tooling to maintain
            quality at scale.
          </p>
        </div>
      </ContentSection>

      {/* Components */}
      <ContentSection
        id="components"
        title="Key Components"
        description="A complete API management solution includes these core components."
        background="muted"
      >
        <FeatureShowcase features={components} columns={3} variant="cards" />
      </ContentSection>

      {/* Lifecycle */}
      <ContentSection
        id="lifecycle"
        title="The API Lifecycle"
        description="APIs go through distinct phases from creation to retirement."
      >
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border md:left-1/2 md:-translate-x-1/2" />
          <div className="space-y-8">
            {lifecycle.map((item, index) => (
              <div key={item.phase} className="relative flex gap-4 md:gap-8">
                <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                  {index + 1}
                </div>
                <div className="ml-16 md:ml-0 md:w-1/2 md:pr-8 md:text-right md:odd:order-1 md:even:pl-8 md:even:text-left md:even:pr-0">
                  <h3 className="font-semibold">{item.phase}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      {/* Best Practices */}
      <ContentSection
        id="best-practices"
        title="Best Practices"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Design First</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Define your API contract before implementation. Use specifications like OpenAPI
              to enable parallel development and ensure consistency.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Version Strategically</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Plan for API evolution from the start. Use semantic versioning and maintain
              backward compatibility when possible.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Secure by Default</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Implement security at every layer. Use authentication, authorization, encryption,
              and input validation as standard practices.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Measure Everything</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Track usage, performance, and errors. Use analytics to understand how your APIs
              are used and where to focus improvements.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Related Concepts */}
      <ContentSection id="related" title="Related Concepts">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/concepts/api-gateway"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">API Gateway</h4>
            <p className="text-sm text-muted-foreground mt-1">
              The runtime component of API management
            </p>
          </Link>
          <Link
            to="/concepts/data-integration"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Data Integration</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Connecting data across systems
            </p>
          </Link>
          <Link
            to="/features/integrations"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">2000+ Integrations</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Pre-built API integrations
            </p>
          </Link>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Simplify Your API Management"
        description="Matricks provides complete API management with 2000+ pre-built integrations, security policies, and real-time analytics."
        primaryAction={{ label: "Get Started", href: "/login" }}
        secondaryAction={{ label: "View Features", href: "/features" }}
        background="primary"
      />
    </div>
  );
}
